/**
 * Resolves Postgres `coverage.service_type` for treatment tabs and update keys.
 *
 * @module coverageInfo/helpers/resolveCoverageServiceType
 */

import type {
  CoverageResult,
  PatientResponsibility,
  Payload,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";

/**
 * Returns true when a service type code is present and not a placeholder.
 *
 * @param value - Raw service type from API or UI
 * @returns Whether the code can identify a coverage row
 */
export const isUsableCoverageServiceType = (
  value: string | undefined | null,
): boolean => {
  const trimmed = (value ?? "").trim();
  return trimmed !== "" && trimmed !== "-";
};

/**
 * Resolves the service type code for one coverage row (matches Postgres UPDATE key).
 *
 * @param coverage - Coverage table row for one treatment
 * @param coverageResult - Index-aligned coverage_result row, if any
 * @param payload - Order payload; only used when a single treatment row exists
 * @param allowPayloadFallback - When false, never use order-level service types
 * @returns Service type code for tabs and `service_type` filter on save
 */
export const resolveCoverageServiceTypeCode = (
  coverage: SingleCoverage,
  coverageResult: CoverageResult | undefined,
  payload?: Payload,
  allowPayloadFallback = true,
): string => {
  if (isUsableCoverageServiceType(coverage.service_type)) {
    return String(coverage.service_type).trim();
  }

  const pr = coverage.patient_responsibility as
    | (PatientResponsibility & { service_type?: string })
    | undefined;
  if (isUsableCoverageServiceType(pr?.service_type)) {
    return String(pr?.service_type).trim();
  }

  if (
    coverageResult &&
    isUsableCoverageServiceType(coverageResult.service_type)
  ) {
    return String(coverageResult.service_type).trim();
  }

  const request = coverageResult?.coverage_request;
  if (request && typeof request === "object") {
    const fromRequest =
      (request as Record<string, unknown>).serviceType ??
      (request as Record<string, unknown>).service_type;
    if (isUsableCoverageServiceType(String(fromRequest ?? ""))) {
      return String(fromRequest).trim();
    }
  }

  if (!allowPayloadFallback) {
    return "";
  }

  const rawPayloadType = payload?.service_type;
  if (Array.isArray(rawPayloadType) && rawPayloadType.length > 0) {
    const first = String(rawPayloadType[0] ?? "").trim();
    return isUsableCoverageServiceType(first) ? first : "";
  }
  if (!Array.isArray(rawPayloadType) && rawPayloadType) {
    const single = String(rawPayloadType).trim();
    return isUsableCoverageServiceType(single) ? single : "";
  }

  return "";
};

/**
 * Sort comparator for treatment tab order (numeric codes sort numerically).
 *
 * @param left - First service type code
 * @param right - Second service type code
 * @returns Comparison result for `Array.prototype.sort`
 */
export const compareCoverageServiceTypeCodes = (
  left: string,
  right: string,
): number => {
  const a = left.trim();
  const b = right.trim();
  const na = Number.parseInt(a, 10);
  const nb = Number.parseInt(b, 10);
  if (Number.isFinite(na) && Number.isFinite(nb)) {
    return na - nb;
  }
  return a.localeCompare(b, undefined, { numeric: true });
};
